import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchGifsQuery } from "queries";
import { TopicSelector } from "components/explore/TopicSelector";
import { GifList, IUserList } from "components/feed/GifList";
import { SuggestedNiches } from "components/widgets/SuggestedNiches";
import { useQuery } from "hooks/useQuery";

import { IGif, INicheMap } from "services/types";
import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { filterUsersList } from "utils/filterUsersList";
import { routes } from "routes";
import { ViewRegion } from "utils/metrics";

import "./ExploreImagesPage.scss";
import { ContentPageSkyscraperEnum } from "components/ads/config/units/contentPageSkyscraper";
import { WithSideAds } from "components/ads";

export const ExploreImagesPage = (): JSX.Element => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [order, setOrder] = useState<string>("trending");
  const [tiles, setTiles] = useState<boolean>(true);
  const [verified, setVerified] = useState<boolean>(false);
  const [niches, setNiches] = useState<INicheMap>({});
  const navigate = useNavigate();
  const query = useQuery();

  const { isSuccess, isLoading, isError, data } = useSearchGifsQuery({
    order,
    count: 30,
    page,
    type: "i",
    verified,
  });

  useEffect(() => {
    document.title = "Top Images | RedGIFs";
  }, []);

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setPage(data.page);
      setPages(data.pages);
      setGifs(prev => filterGifsList(prev, data.gifs));
      setUsers(prev => filterUsersList(prev, data.users));

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [isSuccess, data]);

  useEffect(() => {
    const orderParam = query.get("order");
    const verifiedParam = query.get("verified");

    if (orderParam) {
      changeOrder(orderParam);
    }

    if (verifiedParam) {
      changeVerified(!!verifiedParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeOrder = useCallback((value: string): void => {
    const verifiedParam = !!query.get("verified") || verified;

    navigate(routes.exploreImages(value, verifiedParam));
    setOrder(value);
    setGifs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verified]);

  const fetchMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const changeTopic = useCallback(() => { }, []);

  const changeMode = useCallback((tiles: boolean) => {
    setTiles(tiles);
  }, []);

  const changeVerified = useCallback((value: boolean) => {
    const orderParam = query.get("order") || order;
    navigate(routes.exploreImages(orderParam, value));
    setVerified(value);
    setGifs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const injector = useCallback((pos: number): JSX.Element => {
    if (pos === 3) {
      return <SuggestedNiches page={1}/>;
    } else {
      return <></>;
    }
  }, []);

  return (
    <Fragment>
      <WithSideAds
        leftAds={[
          ContentPageSkyscraperEnum.ContentPageSkyscraperL1,
        ]}
        rightAds={[
          ContentPageSkyscraperEnum.ContentPageSkyscraperR1,
        ]}
      >
        <div className="wide page exploreImagesPage">
          <TopicSelector active="images" />

          <GifList
            ordering={true}
            orderingTop={true}
            topics={false}
            manage={false}

            topic="Images"
            order={order}
            tiles={tiles}
            verified={verified}
            gifs={gifs}
            users={users}
            niches={niches}
            totalLength={gifs.length}
            hasMore={page < pages}
            changeTopic={changeTopic}
            changeOrder={changeOrder}
            changeMode={changeMode}
            changeVerified={changeVerified}
            fetchMore={fetchMore}
            isError={isError}
            isLoading={isLoading}
            injector={injector}
            source={ViewRegion.Explore}
          />
        </div>
      </WithSideAds>
    </Fragment>
  );
};
